import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Youtube, ExternalLink } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { YOUTUBE_GAMEDEV } from '../data/divisions';
import './VideoCarousel.css';

export interface VideoItem {
  id: string;
  src: string;
  titleKey?: string;
  titleDefault: string;
}

const DEFAULT_VIDEOS: VideoItem[] = [
  { id: '0', src: '/resources/aTicYBRGvjqk.mp4', titleKey: 'game.trailer0', titleDefault: 'Trailer 01 - Gameplay Teaser' },
  { id: '1', src: '/resources/caXMgQPeGvsF.mp4', titleKey: 'game.trailer1', titleDefault: 'Trailer 02 - Core Gameplay' },
  { id: '2', src: '/resources/cnxSDJoYTMGf.mp4', titleKey: 'game.trailer2', titleDefault: 'Trailer 03 - Splitscreen Match' },
  { id: '3', src: '/resources/dmvTIUrjsWBV.mp4', titleKey: 'game.trailer3', titleDefault: 'Trailer 04 - Action Highlights' },
  { id: '4', src: '/resources/gYIjuBamEqbh.mp4', titleKey: 'game.trailer4', titleDefault: 'Trailer 05 - Combat Arena' },
  { id: '5', src: '/resources/hPtUxZHgzLJp.mp4', titleKey: 'game.trailer5', titleDefault: 'Trailer 06 - Flight & Hangar' },
  { id: '6', src: '/resources/hWSKBauTPjkF.mp4', titleKey: 'game.trailer6', titleDefault: 'Trailer 07 - Night Ops' },
  { id: '7', src: '/resources/jSkxuwDqWItV.mp4', titleKey: 'game.trailer7', titleDefault: 'Trailer 08 - Swarm Overwatch' },
  { id: '8', src: '/resources/KkYfTUxqAyVF.mp4', titleKey: 'game.trailer8', titleDefault: 'Trailer 09 - Tactical Push' },
  { id: '9', src: '/resources/RbiGTShoHymp.mp4', titleKey: 'game.trailer9', titleDefault: 'Trailer 10 - Showdown Highlight' },
  { id: '10', src: '/resources/trailer_01.mp4', titleKey: 'game.trailer10', titleDefault: 'Trailer 11 - Arena Assault' },
  { id: '11', src: '/resources/trailer_04.mp4', titleKey: 'game.trailer11', titleDefault: 'Trailer 12 - Squad Warfare' },
  { id: '12', src: '/resources/trailer_06.mp4', titleKey: 'game.trailer12', titleDefault: 'Trailer 13 - High Impact' },
  { id: '13', src: '/resources/trailer_07.mp4', titleKey: 'game.trailer13', titleDefault: 'Trailer 14 - Precision Drop' },
  { id: '14', src: '/resources/trailer_0cn2bhk51o.mp4', titleKey: 'game.trailer14', titleDefault: 'Trailer 15 - Sector Sweep' },
  { id: '15', src: '/resources/trailer_0wfuw1tra8.mp4', titleKey: 'game.trailer15', titleDefault: 'Trailer 16 - Urban Skirmish' },
  { id: '16', src: '/resources/trailer_10.mp4', titleKey: 'game.trailer16', titleDefault: 'Trailer 17 - Night Engagement' },
  { id: '17', src: '/resources/trailer_11.mp4', titleKey: 'game.trailer17', titleDefault: 'Trailer 18 - Rapid Fire' },
  { id: '18', src: '/resources/trailer_13.mp4', titleKey: 'game.trailer18', titleDefault: 'Trailer 19 - Base Defense' },
  { id: '19', src: '/resources/trailer_15.mp4', titleKey: 'game.trailer19', titleDefault: 'Trailer 20 - Final Stand' },
  { id: '20', src: '/resources/trailer_40wrlvllfy.mp4', titleKey: 'game.trailer20', titleDefault: 'Trailer 21 - Frontline Clash' },
  { id: '21', src: '/resources/trailer_63btzajw0n.mp4', titleKey: 'game.trailer21', titleDefault: 'Trailer 22 - Stealth Extraction' },
  { id: '22', src: '/resources/trailer_6okrb3tiuf.mp4', titleKey: 'game.trailer22', titleDefault: 'Trailer 23 - Heavy Armor Push' },
  { id: '23', src: '/resources/trailer_78i9buv6ee.mp4', titleKey: 'game.trailer23', titleDefault: 'Trailer 24 - Sky Control' },
  { id: '24', src: '/resources/trailer_af6wzdgazp.mp4', titleKey: 'game.trailer24', titleDefault: 'Trailer 25 - Rapid Infiltration' },
  { id: '25', src: '/resources/trailer_b0afnbup42.mp4', titleKey: 'game.trailer25', titleDefault: 'Trailer 26 - Tactical Strike' },
  { id: '26', src: '/resources/trailer_gw896ekqph.mp4', titleKey: 'game.trailer26', titleDefault: 'Trailer 27 - Dreadnought Siege' },
  { id: '27', src: '/resources/trailer_iw84ke4ssj.mp4', titleKey: 'game.trailer27', titleDefault: 'Trailer 28 - Low-Altitude Duel' },
  { id: '28', src: '/resources/trailer_m9mfod3bai.mp4', titleKey: 'game.trailer28', titleDefault: 'Trailer 29 - Sudden Ambush' },
  { id: '29', src: '/resources/trailer_o801ashfyg.mp4', titleKey: 'game.trailer29', titleDefault: 'Trailer 30 - Command Patrol' },
  { id: '30', src: '/resources/trailer_u0l0c5p93h.mp4', titleKey: 'game.trailer30', titleDefault: 'Trailer 31 - Overdrive Combat' },
  { id: '31', src: '/resources/trailer_ybnj588b6r.mp4', titleKey: 'game.trailer31', titleDefault: 'Trailer 32 - Swarm Intercept' },
  { id: '32', src: '/resources/trailer_yz6nlod3rk.mp4', titleKey: 'game.trailer32', titleDefault: 'Trailer 33 - Apex Dogfight' },
  { id: '33', src: '/resources/VKudxIGoRPTt.mp4', titleKey: 'game.trailer33', titleDefault: 'Trailer 34 - Victory Lap' },
];

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function useItemsPerPage() {
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    if (typeof window === 'undefined') return 3;
    return window.innerWidth <= 992 ? 1 : 3;
  });

  useEffect(() => {
    const handleResize = () => {
      setItemsPerPage(window.innerWidth <= 992 ? 1 : 3);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return itemsPerPage;
}

export function VideoCarousel({ videos: customVideos }: { videos?: VideoItem[] }) {
  const { t } = useI18n();

  // Shuffled full list of videos on mount / F5
  const [videos] = useState<VideoItem[]>(() => {
    const list = customVideos || DEFAULT_VIDEOS;
    return shuffleArray(list);
  });

  const itemsPerPage = useItemsPerPage();
  const isMobile = itemsPerPage === 1;
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const videoRefsMap = useRef<Map<number, HTMLVideoElement>>(new Map());

  const total = videos.length;
  const totalPages = Math.ceil(total / itemsPerPage);
  const currentPage = Math.floor(currentIndex / itemsPerPage);

  // Keep currentIndex valid when itemsPerPage changes
  useEffect(() => {
    if (currentIndex >= total) {
      setCurrentIndex(0);
    }
  }, [itemsPerPage, total, currentIndex]);

  // Compute ONLY the visible slice of videos (Max 3 on desktop, 1 on mobile)
  const visibleSlice = videos.slice(currentIndex, currentIndex + itemsPerPage);

  // Sound logic when visible slice or currentIndex changes
  useEffect(() => {
    if (isMobile) {
      const el = videoRefsMap.current.get(0);
      if (el) {
        el.muted = false;
        const playPromise = el.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            el.muted = true;
            el.play().catch(() => {});
          });
        }
      }
    }
  }, [currentIndex, isMobile]);

  // On Desktop: when one video is unmuted manually, mute all other visible videos
  const handleVolumeChange = (relIdx: number) => {
    const targetEl = videoRefsMap.current.get(relIdx);
    if (targetEl && !targetEl.muted) {
      videoRefsMap.current.forEach((otherEl, key) => {
        if (key !== relIdx && otherEl) {
          otherEl.muted = true;
        }
      });
    }
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => {
      const nextIdx = prev - itemsPerPage;
      if (nextIdx < 0) {
        const lastPageIdx = (totalPages - 1) * itemsPerPage;
        return lastPageIdx >= total ? 0 : lastPageIdx;
      }
      return nextIdx;
    });
  };

  const handleNext = () => {
    setCurrentIndex((prev) => {
      const nextIdx = prev + itemsPerPage;
      return nextIdx >= total ? 0 : nextIdx;
    });
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (diff > 40) handleNext();
    else if (diff < -40) handlePrev();
    touchStartX.current = null;
  };

  const slideWidthPercent = 100 / itemsPerPage;

  return (
    <div className="video-carousel-container">
      {totalPages > 1 && (
        <div className="vc-header">
          <div className="vc-nav-buttons">
            <button
              type="button"
              className="vc-nav-btn"
              onClick={handlePrev}
              aria-label="Previous videos"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              className="vc-nav-btn"
              onClick={handleNext}
              aria-label="Next videos"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Dynamic Grid Stage */}
      <div
        className="vc-track-viewport"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="vc-dynamic-grid">
          {visibleSlice.map((item, relIdx) => {
            return (
              <div
                className="vc-slide-dynamic"
                key={item.id}
                style={{
                  flex: `0 0 ${slideWidthPercent}%`,
                  maxWidth: `${slideWidthPercent}%`,
                }}
              >
                <div className="vc-pure-card">
                  <video
                    ref={(el) => {
                      if (el) videoRefsMap.current.set(relIdx, el);
                      else videoRefsMap.current.delete(relIdx);
                    }}
                    src={item.src}
                    controls
                    autoPlay
                    loop
                    muted={isMobile ? false : true}
                    playsInline
                    preload="metadata"
                    className="vc-pure-video"
                    onVolumeChange={() => handleVolumeChange(relIdx)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="vc-dots">
          {Array.from({ length: totalPages }).map((_, pageIdx) => (
            <button
              key={pageIdx}
              type="button"
              className={`vc-dot ${pageIdx === currentPage ? 'active' : ''}`}
              onClick={() => setCurrentIndex(pageIdx * itemsPerPage)}
              aria-label={`Go to page ${pageIdx + 1}`}
            />
          ))}
        </div>
      )}

      {/* Bottom YouTube Channel Link */}
      <div className="vc-yt-container">
        <a
          href={YOUTUBE_GAMEDEV}
          target="_blank"
          rel="noreferrer"
          className="vc-yt-btn"
        >
          <Youtube size={22} className="vc-yt-icon" />
          <span className="vc-yt-text">
            {t('game.youtubeChannelLink') || 'Zobacz kanał YT z wszystkimi filmami'}
          </span>
          <ExternalLink size={16} className="vc-yt-arrow" />
        </a>
      </div>
    </div>
  );
}
